import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Info, Loader2 } from "lucide-react";
import PageShell, { PageHero } from "./PageShell";
import OfferCard, { fmtRate, fmtTerm } from "../components/OfferCard";
import api from "../lib/api";

import { tr } from "../i18n";
const TERMS = [12, 24, 36, 48, 60];
const MIN_RATES = [0, 4.5, 5, 5.2];
const SORTS = { rate: "Highest interest rate", min: "Lowest minimum deposit", term: "Term (shortest first)" };

const sorters = {
  rate: (a, b) => b.rate_aer - a.rate_aer,
  min: (a, b) => a.min_deposit - b.min_deposit || b.rate_aer - a.rate_aer,
  term: (a, b) => a.term_months - b.term_months || b.rate_aer - a.rate_aer,
};

const DISCLAIMER =
  "The interest rates shown are for guidance only and may change. The current terms and conditions of the respective provider are decisive. Availability depends, among other things, on place of residence, account requirements, deposit amount and product conditions. Investments in a foreign currency may result in exchange-rate losses. Please check the deposit protection and the applicable terms and conditions of the respective provider before making an investment decision.";

const Select = ({ label, testid, value, onChange, children }) => (
  <label className="block">
    <span className="block text-[12px] uppercase tracking-wide text-[#0b1f3a]/50 mb-1.5">{label}</span>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="rh-input">{children}</select>
  </label>
);

export default function UKDepositsPage() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState(null);
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("all");
  const [minRate, setMinRate] = useState("0");
  const [sort, setSort] = useState("rate");

  useEffect(() => {
    api.get("/uk-offers").then((r) => setOffers(r.data)).catch(() => setOffers([]));
  }, []);

  const list = useMemo(() => {
    if (!offers) return [];
    const s = q.trim().toLowerCase();
    return offers
      .filter((o) => !s || o.bank.toLowerCase().includes(s))
      .filter((o) => term === "all" || o.term_months === Number(term))
      .filter((o) => o.rate_aer >= Number(minRate))
      .sort(sorters[sort]);
  }, [offers, q, term, minRate, sort]);

  const request = (o) =>
    navigate(`/contact?offer=${encodeURIComponent(`${o.bank} – ${o.product_name} – ${fmtTerm(o.term_months)} – ${fmtRate(o.rate_aer)} AER`)}`);

  return (
    <PageShell>
      <PageHero eyebrow={tr("UK Fixed-Term Deposits")} title={tr("UK fixed-term deposit offers")}
        intro={tr("Compare fixed-term deposit accounts from UK banks and building societies by interest rate, term and minimum deposit.")} />
      <section className="bg-[#fbf9f4] py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div data-testid="uk-offers-fx-note" className="flex gap-3 rounded-2xl bg-white ring-1 ring-[#c2a15a]/30 p-5 text-[14px] text-[#0b1f3a]/75 leading-relaxed mb-8">
            <Info size={18} className="shrink-0 mt-0.5 text-[#c2a15a]" />
            <p>{tr("All offers below are held in")} <strong className="text-[#0b1f3a]">{tr("British pounds (GBP)")}</strong>{tr(". For investors whose assets are in euros, changes in the GBP/EUR exchange rate can increase or reduce the value of the deposit and interest when converted back.")}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="uk-offers-controls">
            <label className="block">
              <span className="block text-[12px] uppercase tracking-wide text-[#0b1f3a]/50 mb-1.5">{tr("Search bank")}</span>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0b1f3a]/40" />
                <input data-testid="uk-offers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("e.g. GB Bank")} className="rh-input !pl-9" />
              </div>
            </label>
            <Select label={tr("Term")} testid="uk-offers-term-filter" value={term} onChange={setTerm}>
              <option value="all">{tr("All terms")}</option>
              {TERMS.map((t) => <option key={t} value={t}>{fmtTerm(t)}</option>)}
            </Select>
            <Select label={tr("Minimum interest rate")} testid="uk-offers-rate-filter" value={minRate} onChange={setMinRate}>
              {MIN_RATES.map((r) => <option key={r} value={r}>{r ? tr("{v0}% p.a. or more", { v0: r.toFixed(2) }) : tr("Any rate")}</option>)}
            </Select>
            <Select label={tr("Sort by")} testid="uk-offers-sort" value={sort} onChange={setSort}>
              {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{tr(v)}</option>)}
            </Select>
          </div>

          <p data-testid="uk-offers-count" className="text-[13px] text-[#0b1f3a]/55 mb-5">
            {offers ? list.length === 1 ? tr("1 offer shown") : tr("{n} offers shown", { n: list.length }) : ""}
          </p>

          {!offers ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#c2a15a]" /></div>
          ) : list.length === 0 ? (
            <p data-testid="uk-offers-empty" className="text-center py-16 text-[#0b1f3a]/60">{tr("No offers match your filters.")}</p>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="uk-offers-grid">
              {list.map((o) => <OfferCard key={o.id} o={o} onRequest={request} />)}
            </div>
          )}

          <p data-testid="uk-offers-disclaimer" className="text-[13px] text-[#0b1f3a]/55 leading-relaxed border-t border-[#0b1f3a]/10 pt-6 mt-12 max-w-4xl">
            {tr(DISCLAIMER)}
          </p>
        </div>
      </section>
    </PageShell>
  );
}
